import { useState } from "react";

// prop-type is a library for typechecking of props
import PropTypes from "prop-types";

// formik components
import { ErrorMessage, Field } from "formik";

import { useTheme } from "@mui/material/styles";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftInput from "components/SoftInput";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import SoftSelect from "components/SoftSelect";
import SoftTagInput from "components/SoftTagInput";
import SoftEditor from "components/SoftEditor";
import SoftDatePicker from "components/SoftDatePicker";
import SoftButton from "components/SoftButton";
import {
  ThemedContentCopyIcon,
  ThemedCheckCircleIcon
} from "assets/CXFIcons/Designer/ContextActions";
function FormField({
  options = [],
  label = "",
  type,
  name = "",
  defaultValue = {},
  isMultiline,
  handleLinkClick = Function(),
  style,
  accept,
  ...rest
}) {
  const [isCopied, setIsCopied] = useState(false);
  const theme = useTheme();

  const handleCopyTenantId = () => {
    const tenantId = rest?.value;

    if (tenantId) {
      navigator.clipboard
        .writeText(tenantId)
        .then(() => {
          setIsCopied(true);

          setTimeout(() => {
            setIsCopied(false);
          }, 2000);
        })
        .catch((error) => {
          console.error("Error copying text to clipboard:", error);
        });
    }
  };
  const getInput = () => {
    switch (type) {
      case "text":
      case "file":
      case "number":
      case "password":
        return isMultiline ? (
          <Field
            {...rest}
            type={type}
            name={name}
            as={SoftInput}
            multiline
            rows={rest.rows ? rest.rows : 5}
          />
        ) : (
          <Field
            {...rest}
            type={type}
            name={name}
            as={SoftInput}
            inputProps={{ accept: accept ? accept : undefined }}
          />
        );
      case "id":
        return (
          <Field name={name}>
            {() => {
              return (
                <SoftBox
                  width="100%"
                  display="flex"
                  flexDirection={{ xs: "column", lg: "row" }}
                  justifyContent="space-between"
                  alignItems="center"
                  gap={2}
                >
                  <SoftInput {...rest} />
                  <SoftButton color="primary" variant="outlined" onClick={handleCopyTenantId}>
                    {isCopied ? (
                      <ThemedCheckCircleIcon
                        color={theme.palette.mode === "dark" ? "rgb(170, 139, 253)" : "#7443FF"}
                      />
                    ) : (
                      <ThemedContentCopyIcon
                        color={theme.palette.mode === "dark" ? "rgb(170, 139, 253)" : "#7443FF"}
                      />
                    )}
                  </SoftButton>
                </SoftBox>
              );
            }}
          </Field>
        );
      case "editor":
        return (
          <Field name={name}>
            {({ field, form }) => {
              return (
                <SoftEditor
                  {...rest}
                  value={field.value}
                  onChange={(e) => {
                    const links = document.querySelectorAll(
                      ".ql-editor a[rel='noopener noreferrer']"
                    );
                    if (links.length) {
                      links.forEach((l) => {
                        let href = l.getAttribute("href");
                        if (href.indexOf("http") === -1) {
                          l.setAttribute("href", `http://${href}`);
                        }
                        l.removeEventListener("click", handleLinkClick);
                        l.addEventListener("click", handleLinkClick);
                      });
                    }
                    form.setFieldValue(field.name, e);
                  }}
                />
              );
            }}
          </Field>
        );
      case "tag":
        return (
          <Field name={name}>
            {({ field, form }) => {
              return (
                <SoftTagInput
                  {...rest}
                  value={field.value}
                  onChange={(value) => {
                    form.setFieldValue(field.name, value);
                  }}
                />
              );
            }}
          </Field>
        );
      case "select":
        return (
          <Field name={name}>
            {({ field, form, meta }) => {
              const { touched } = form;

              const isError = Boolean(meta.error) && touched[field.name];

              return (
                <SoftSelect
                  {...rest}
                  defaultValue={defaultValue}
                  options={options}
                  error={isError}
                  onChange={({ value }) => {
                    form.setFieldValue(field.name, value);
                  }}
                  onBlur={() => {
                    form.setFieldTouched(field.name, true);
                  }}
                />
              );
            }}
          </Field>
        );
      case "date":
        return (
          <Field name={name}>
            {({ field, form, meta }) => {
              const { touched } = form;
              const isError = Boolean(meta.error) && touched[field.name];
              return (
                <SoftDatePicker
                  {...rest}
                  className="date-picker"
                  input={{
                    placeholder: "Select a date",
                    error: isError
                  }}
                  value={field.value}
                  onChange={(value, valueStr) => {
                    form.setFieldValue(field.name, valueStr);
                  }}
                  onClose={() => {
                    form.setFieldTouched(field.name, true);
                  }}
                />
              );
            }}
          </Field>
        );
      case "boolean":
        return (
          <Field name={name}>
            {({ field, form }) => {
              return (
                <Switch
                  value={field.value}
                  onChange={(e) => {
                    form.setFieldValue(field.name, e.target.checked);
                  }}
                  color="primary"
                />
              );
            }}
          </Field>
        );
      case "checkboxGroup":
        return (
          <Field name={name}>
            {({ field, form }) => {
              return (
                <SoftBox className="checkbox-group-wrapper">
                  {options.map((opt) => {
                    return (
                      <FormControlLabel
                        key={opt.label}
                        control={
                          <Switch
                            value={opt.value}
                            checked={field?.value?.includes(opt.value)}
                            onChange={(e) => {
                              form.setFieldValue(
                                field.name,
                                e.target.checked
                                  ? [...(field?.value ?? []), opt.value]
                                  : field?.value?.filter((f) => f !== opt.value)
                              );
                            }}
                            color="primary"
                          />
                        }
                        label={opt.label}
                      />
                    );
                  })}
                </SoftBox>
              );
            }}
          </Field>
        );
    }
  };

  return (
    <SoftBox style={style}>
      <SoftBox mb={1.5}>
        {label && (
          <SoftBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
            <SoftTypography
              component="label"
              variant="caption"
              fontWeight="bold"
              textTransform="capitalize"
            >
              {label}
              {rest.required && <span style={{ color: "rgb(255, 102, 124)" }}> *</span>}
            </SoftTypography>
          </SoftBox>
        )}
        {getInput()}
        <SoftBox mt={0.75}>
          <SoftTypography component="div" variant="caption" color="error">
            <ErrorMessage name={name} />
          </SoftTypography>
        </SoftBox>
      </SoftBox>
    </SoftBox>
  );
}

// typechecking props for FormField
FormField.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(PropTypes.object),
  defaultValue: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string
  })
};

export default FormField;
